import { AIConfig } from '../context/AIContext';
import { aiService } from './aiService';

export const aiChatService = {
    getAIResponse: async (message: string, config: AIConfig): Promise<string> => {
        // Simulate network delay for realism
        await new Promise(resolve => setTimeout(resolve, 1000));

        const lower = message.toLowerCase();
        console.log('AI Service received:', lower);

        // 1. Guardrails Check
        if (config.chatbot.guardrails.enabled) {
            const blockedTopic = config.chatbot.guardrails.blockedTopics.find(topic =>
                topic && lower.includes(topic.toLowerCase().trim())
            );

            if (blockedTopic) {
                return `I cannot answer questions about **${blockedTopic}** as it violates our content moderation policy. Please ask about cybersecurity topics.`;
            }
        }

        // 2. RAG Simulation Prefix
        let responsePrefix = "";
        if (config.chatbot.ragEnabled) {
            // Simulate finding relevant documents based on keywords
            const relevantDocs = [];
            if (lower.includes('phishing')) relevantDocs.push('Phishing_Policy_v2.pdf');
            if (lower.includes('password')) relevantDocs.push('Password_Standards_2024.docx');
            if (lower.includes('ransomware')) relevantDocs.push('Incident_Response_Plan.pdf');

            if (relevantDocs.length > 0) {
                responsePrefix = `*[Searching internal knowledge base... Found relevant documents: ${relevantDocs.join(', ')}]*\n\n`;
            } else {
                responsePrefix = `*[Searching internal knowledge base... No specific internal documents found, using general knowledge]*\n\n`;
            }
        }

        // 3. Local Model Simulation
        if (config.chatbot.provider === 'local') {
            return `${responsePrefix}**[Local Model Response]**\n\nI am running locally at \`${config.chatbot.localModel.url}\`.\n\nI processed your request: "${message}"`;
        }

        // 4. Real AI Integration or Fallback
        if (config?.textGeneration?.enabled) {
            try {
                console.log('[AI Chat] Attempting to use real AI provider:', config.textGeneration.provider);
                const aiResponse = await aiService.generateChatResponse(message, config);
                return responsePrefix + aiResponse;
            } catch (error) {
                console.error('[AI Chat] Real AI generation failed, falling back to mock:', error);
                // Fall through to mock logic below
            }
        }

        // 5. Standard Mock Logic (Fallback)
        let response = "";

        // Check for greetings with word boundaries
        if (/\b(hello|hi|hey)\b/.test(lower)) {
            response = "Hello! How can I assist you with your cybersecurity needs today?";
        } else if (lower.includes('phishing') || lower.includes('scam')) {
            response = `**Phishing** is a cyberattack where criminals send fraudulent emails pretending to be from legitimate organizations.

**Warning Signs:**
• Urgent language
• Suspicious sender
• Requests for sensitive info
• Poor grammar

**Prevention:**
1. Verify sender identity
2. Don't click suspicious links
3. Use 2FA
4. Report suspicious emails`;
        } else if (lower.includes('password') || lower.includes('credential')) {
            response = `**Strong Password Tips:**

• 12+ characters long
• Mix uppercase, lowercase, numbers, symbols
• Avoid personal information
• Use unique passwords
• Consider passphrases

**Management:**
• Use password manager
• Enable 2FA
• Never share passwords
• Change if compromised`;
        } else if (lower.includes('ransomware') || lower.includes('ransom') || lower.includes('malware') || lower.includes('virus')) {
            response = `**Ransomware** encrypts your files and demands payment.

**Prevention:**
1. Regular backups (3-2-1 rule)
2. Keep software updated
3. Use antivirus
4. Train employees
5. Network segmentation

**If Infected:**
• Don't pay ransom
• Isolate systems
• Contact experts
• Restore from backups`;
        } else if (lower.includes('2fa') || lower.includes('two-factor') || lower.includes('authentication') || lower.includes('mfa')) {
            response = `**Two-Factor Authentication (2FA)** adds extra security.

**Types:**
• SMS codes
• Authenticator apps
• Hardware tokens
• Biometrics

**Benefits:**
• 99.9% reduction in unauthorized access
• Protects even if password stolen

**Best Practice:**
Use authenticator apps over SMS when possible.`;
        } else if (lower.includes('bec') || lower.includes('business email')) {
            response = `**Business Email Compromise (BEC)** is when attackers impersonate executives.

**Common Tactics:**
• CEO Fraud
• Invoice scams
• Account compromise

**Prevention:**
1. Verify requests via phone
2. Dual authorization for payments
3. Email authentication (SPF, DKIM)
4. Train employees
5. Flag external emails`;
        } else if (lower.includes('wifi') || lower.includes('wireless') || lower.includes('network')) {
            response = `**WiFi Security Tips:**

**Secure Your Network:**
1. Change default password
2. Enable WPA3/WPA2
3. Hide SSID
4. Update router firmware
5. Disable WPS
6. Use guest network

**Public WiFi:**
• Always use VPN
• Avoid sensitive transactions
• Disable auto-connect
• Use HTTPS sites`;
        } else if (lower.includes('help') || lower.includes('topics') || lower.includes('support')) {
            response = `I can help with:

• **Phishing** - Email scams
• **Passwords** - Strong passwords & 2FA
• **Ransomware** - Protection & response
• **BEC** - Business email compromise
• **WiFi Security** - Network protection
• **Training** - Available courses
• **Resources** - Downloadable guides

What would you like to know?`;
        } else if (lower.includes('training') || lower.includes('course') || lower.includes('learn')) {
            response = `Visit our **Training** page for courses on:

• Anti-Phishing Training
• BEC Awareness
• Incident Response
• Password Security
• Social Engineering Defense
• Compliance & Regulations

All modules include interactive simulations!`;
        } else if (lower.includes('resource') || lower.includes('download') || lower.includes('guide') || lower.includes('template')) {
            response = `Check the **Resources** page for:

• SME Security Checklist
• Incident Response Template
• Employee Security Guide
• Vendor Assessment Template
• Password Policy Template
• Remote Work Guidelines

All free for SBAJ members!`;
        } else {
            response = "I'm not sure I understand. I can help with phishing, passwords, ransomware, BEC, WiFi security, and more! Try asking about specific cybersecurity topics or type 'help' to see all topics.";
        }

        return responsePrefix + response;
    }
};
